import { SubTrigger as RadixSubTrigger } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { forwardRef } from 'react';

import {
  _mergeProps,
  type AriaLabelingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { ChevronRightSmallIcon } from '@dynatrace/strato-icons';

import { useIsMenuItemHovered } from './hooks/useIsMenuItemHovered.js';
import { getItemSlots } from './item-slots.js';
import * as styles from './Menu.sty.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { textStyleCSS } from '../../styles/textStyle.sty.js';

/**
 * @public
 */
export interface MenuSubTriggerProps extends WithChildren, AriaLabelingProps {
  /**
   * Whether the menu trigger item is disabled.
   * If true, it prevents the user from interacting with the item.
   */
  disabled?: boolean;

  /**
   * Optional text used for typeahead purposes. By default, the typeahead
   * behavior will use the .textContent of the item. Use this when the content
   * is complex, or you have non-textual content inside.
   */
  textValue?: string;
}

/**
 * An item that opens a submenu. Used in combination with a `Menu.Sub`.
 * Must not be rendered inside a root Menu.
 * @public
 */
export const SubTrigger = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuSubTriggerProps
>(
  (
    {
      children,
      disabled,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      ...remainingProps
    }: MenuSubTriggerProps,
    forwardedRef,
  ) => {
    const slots = getItemSlots(children);

    const { focusProps, focusClassName } = useFocusRing({
      disabled,
    });
    const { isItemHovered, hoverProps } = useIsMenuItemHovered();

    return (
      <RadixSubTrigger
        ref={forwardedRef}
        disabled={disabled}
        className={clsx(
          textStyleCSS({ textStyle: 'base-emphasized' }),
          !isItemHovered && focusClassName,
          styles.item({ disabled: disabled }),
        )}
        {..._mergeProps(remainingProps, focusProps, hoverProps)}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        aria-haspopup={true}
        role="menuitem"
        asChild
      >
        <Flex justifyContent="space-between">
          {slots.icon}
          <div className={styles.subChildren}>{slots.freeform}</div>
          <ChevronRightSmallIcon />
        </Flex>
      </RadixSubTrigger>
    );
  },
);

(SubTrigger as typeof SubTrigger & { displayName: string }).displayName =
  'Menu.SubTrigger';
